import { TierLevel } from '@zorro/clients';
import { CheckboxGroup } from '@zorro/design-system/v2/Checkbox';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { UnderlineTooltip } from '@zorro/design-system/v2/Tooltip';
import { YesNo, planTooltipLabels } from '@zorro/types';
import { Box, Flex, Stack } from '@zorro/web/design-system';
import { Fragment, ReactNode, useState } from 'react';
import { $enum } from 'ts-enum-util';

import { FormFooter } from '../FormFooter';
import {
  BooleanFilters,
  CarrierFilters,
  EnrollmentEffortFilters,
  MetalFilters,
  NetworkFilters,
  PlanMarketFilters,
} from './Filters';
import {
  Eligibility,
  FilterGroupMap,
  FilterGroups,
  UniqueValueFilterGroup,
} from './PlanFilterUtils';

const eligibilityFilterValues = $enum(Eligibility).getValues();
const yesNoFilterValues = $enum(YesNo).getValues();

const PANEL_SECTION_ORDER = [
  FilterGroups.CARRIER_NAME,
  FilterGroups.LEVEL,
  FilterGroups.PLAN_TYPE,
  FilterGroups.PLAN_MARKET,
  FilterGroups.HSA_ELIGIBLE,
  FilterGroups.SELF_ENROLL,
  FilterGroups.ENROLLMENT_EFFORT,
];

const SHEET_SECTION_ORDER = [
  FilterGroups.CARRIER_NAME,
  FilterGroups.LEVEL,
  FilterGroups.PLAN_TYPE,
  FilterGroups.HSA_ELIGIBLE,
  FilterGroups.PLAN_MARKET,
  FilterGroups.SELF_ENROLL,
  FilterGroups.ENROLLMENT_EFFORT,
];

type Props = {
  allFilters: UniqueValueFilterGroup;
  mapCarrierNameToLogoUrl: Map<string, string>;
  selectedFilters: FilterGroupMap;
  onSubmit: (value: FilterGroupMap) => void;
  onClear?: () => void;
  /**
   * `sheet` drops the full-height sizing and routes the footer into the
   * surrounding drawer's footer slot, so the form fits a `BottomDrawer`.
   * `panel` keeps the fixed-position footer of the full-height side `Drawer`.
   */
  variant?: 'panel' | 'sheet';
};

export const PlanFilter = ({
  allFilters: availableFilters,
  mapCarrierNameToLogoUrl,
  selectedFilters,
  onSubmit,
  onClear,
  variant = 'panel',
}: Props) => {
  const isSheet = variant === 'sheet';
  const [filters, setFilters] = useState<FilterGroupMap>(selectedFilters);
  const [isSubmitFilterButtonDisabled, setIsSubmitFilterButtonDisabled] =
    useState(true);

  const handleFilterChange = (filterGroup: FilterGroups, values: string[]) => {
    let filterValues: string[] = [...values];
    if (filterGroup === FilterGroups.LEVEL) {
      if (filterValues.includes(TierLevel.bronze)) {
        filterValues.push(TierLevel.expanded_bronze);
      } else {
        filterValues = filterValues.filter(
          (value) => value !== TierLevel.expanded_bronze
        );
      }
    }
    setFilters((prev: FilterGroupMap) => ({
      ...prev,
      [filterGroup]: filterValues,
    }));
    setIsSubmitFilterButtonDisabled(false);
  };

  const handleClearFilters = () => {
    setFilters({
      [FilterGroups.CARRIER_NAME]: [],
      [FilterGroups.HSA_ELIGIBLE]: [],
      [FilterGroups.LEVEL]: [],
      [FilterGroups.PLAN_MARKET]: [],
      [FilterGroups.PLAN_TYPE]: [],
      [FilterGroups.SELF_ENROLL]: [],
      [FilterGroups.ENROLLMENT_EFFORT]: [],
    });
    setIsSubmitFilterButtonDisabled(false);
    onClear?.();
  };

  const handleSubmit = () => {
    setIsSubmitFilterButtonDisabled(true);
    onSubmit(filters);
  };

  const isClearButtonDisabled = !Object.values(filters).some(
    (filtersByGroup) => filtersByGroup.length > 0
  );

  const sheetFooter = isSheet ? (
    <FormFooter
      withPaper={false}
      primaryLabel="Save filters"
      primaryButtonProps={{
        onClick: handleSubmit,
        disabled: isSubmitFilterButtonDisabled,
        fullWidth: true,
      }}
      secondaryLabel="Clear all"
      secondaryButtonProps={{
        onClick: handleClearFilters,
        disabled: isClearButtonDisabled,
        variant: 'tertiary',
        fullWidth: true,
      }}
    />
  ) : null;

  const isInDrawer = useDrawerFooter(sheetFooter);

  const sectionsByGroup: Partial<Record<FilterGroups, ReactNode>> = {
    [FilterGroups.CARRIER_NAME]: (
      <CheckboxGroup
        value={filters[FilterGroups.CARRIER_NAME]}
        label="Carriers"
        onChange={(values) =>
          handleFilterChange(FilterGroups.CARRIER_NAME, values)
        }
      >
        <Flex>
          <CarrierFilters
            filterOptions={availableFilters[FilterGroups.CARRIER_NAME]}
            mapCarrierNameToLogo={mapCarrierNameToLogoUrl}
            selectedFilters={filters[FilterGroups.CARRIER_NAME]}
          />
        </Flex>
      </CheckboxGroup>
    ),

    [FilterGroups.LEVEL]: (
      <CheckboxGroup
        value={filters[FilterGroups.LEVEL]}
        label={
          <UnderlineTooltip
            label={planTooltipLabels.metalLevel}
            textProps={{ size: 'sm' }}
          >
            Metal level
          </UnderlineTooltip>
        }
        onChange={(values) => handleFilterChange(FilterGroups.LEVEL, values)}
      >
        <Box pt="0.25rem">
          <MetalFilters
            filterOptions={availableFilters[FilterGroups.LEVEL]}
            selectedFilters={filters[FilterGroups.LEVEL]}
          />
        </Box>
      </CheckboxGroup>
    ),

    [FilterGroups.PLAN_TYPE]: (
      <CheckboxGroup
        value={filters[FilterGroups.PLAN_TYPE]}
        label="Network type"
        onChange={(values) =>
          handleFilterChange(FilterGroups.PLAN_TYPE, values)
        }
      >
        <Box>
          <NetworkFilters
            filterOptions={availableFilters[FilterGroups.PLAN_TYPE]}
            selectedFilters={filters[FilterGroups.PLAN_TYPE]}
          />
        </Box>
      </CheckboxGroup>
    ),

    [FilterGroups.PLAN_MARKET]: (
      <CheckboxGroup
        value={filters[FilterGroups.PLAN_MARKET]}
        label={
          <UnderlineTooltip
            label={planTooltipLabels.planMarket}
            textProps={{ size: 'sm' }}
          >
            Plan market
          </UnderlineTooltip>
        }
        onChange={(values) =>
          handleFilterChange(FilterGroups.PLAN_MARKET, values)
        }
      >
        <Box pt="0.25rem">
          <PlanMarketFilters
            filterOptions={availableFilters[FilterGroups.PLAN_MARKET]}
            selectedFilters={filters[FilterGroups.PLAN_MARKET]}
          />
        </Box>
      </CheckboxGroup>
    ),

    [FilterGroups.HSA_ELIGIBLE]: (
      <CheckboxGroup
        value={filters[FilterGroups.HSA_ELIGIBLE]}
        label={
          <UnderlineTooltip
            label={planTooltipLabels.hsa}
            textProps={{ size: 'sm' }}
          >
            HSA
          </UnderlineTooltip>
        }
        onChange={(values) => {
          handleFilterChange(FilterGroups.HSA_ELIGIBLE, values);
        }}
      >
        <Box pt="0.25rem">
          <BooleanFilters
            filterValues={eligibilityFilterValues}
            filterOptions={availableFilters[FilterGroups.HSA_ELIGIBLE]}
            selectedFilters={filters[FilterGroups.HSA_ELIGIBLE]}
          />
        </Box>
      </CheckboxGroup>
    ),

    [FilterGroups.SELF_ENROLL]: availableFilters[FilterGroups.SELF_ENROLL]
      .size > 1 && (
      <CheckboxGroup
        value={filters[FilterGroups.SELF_ENROLL]}
        label={
          <UnderlineTooltip
            label={planTooltipLabels.selfEnroll}
            textProps={{ size: 'sm' }}
          >
            Self-enroll required
          </UnderlineTooltip>
        }
        onChange={(values) => {
          handleFilterChange(FilterGroups.SELF_ENROLL, values);
        }}
      >
        <Box pt="0.25rem">
          <BooleanFilters
            filterValues={yesNoFilterValues}
            filterOptions={availableFilters[FilterGroups.SELF_ENROLL]}
            selectedFilters={filters[FilterGroups.SELF_ENROLL]}
          />
        </Box>
      </CheckboxGroup>
    ),

    [FilterGroups.ENROLLMENT_EFFORT]: availableFilters[
      FilterGroups.ENROLLMENT_EFFORT
    ].size > 1 && (
      <CheckboxGroup
        value={filters[FilterGroups.ENROLLMENT_EFFORT]}
        label="Enrollment preferences"
        onChange={(values) => {
          handleFilterChange(FilterGroups.ENROLLMENT_EFFORT, values);
        }}
      >
        <Box pt="0.25rem">
          <EnrollmentEffortFilters
            filterOptions={availableFilters[FilterGroups.ENROLLMENT_EFFORT]}
            selectedFilters={filters[FilterGroups.ENROLLMENT_EFFORT]}
          />
        </Box>
      </CheckboxGroup>
    ),
  };

  const sections = (isSheet ? SHEET_SECTION_ORDER : PANEL_SECTION_ORDER)
    .map((filterGroup) => [filterGroup, sectionsByGroup[filterGroup]] as const)
    .filter(([, section]) => Boolean(section));

  return (
    <Box h={isSheet ? undefined : '100%'}>
      <Stack
        gap={isSheet ? 'var(--space-32)' : undefined}
        mih={isSheet ? undefined : 'calc(100vh - 156px)'}
        pt={isSheet ? undefined : '1rem'}
        pb={isSheet ? undefined : '6rem'}
      >
        {sections.map(([filterGroup, section], index) =>
          isSheet ? (
            <Fragment key={filterGroup}>{section}</Fragment>
          ) : (
            <Stack key={filterGroup} pt={index === 0 ? undefined : '2.5rem'}>
              {section}
            </Stack>
          )
        )}
      </Stack>
      {isSheet ? (
        !isInDrawer && sheetFooter
      ) : (
        <FormFooter
          // eslint-disable-next-line id-length
          paperProps={{ p: '1rem 2rem' }}
          primaryLabel="Save changes"
          primaryButtonProps={{
            onClick: handleSubmit,
            disabled: isSubmitFilterButtonDisabled,
          }}
          secondaryLabel="Clear all"
          secondaryButtonProps={{
            onClick: handleClearFilters,
            disabled: isClearButtonDisabled,
          }}
        />
      )}
    </Box>
  );
};
